import React from 'react';
import JokesSwipeWrapperContainer from '@moment/components/Jokes/JokesSwipeWrapperContainer.tsx';
import WordOfTheDayWrapper from '@moment/components/WordOfTheDay/WordOfTheDayWrapper.tsx';
import QuotesWrapper from '@moment/components/Quotes/QuotesWrapper.tsx';
import FortuneCookieWrapper from '@moment/components/FortuneCookie/FortuneCookieWrapper.tsx';
import CarouselWrapper from '@moment/components/Carousel/CarouselWrapper.tsx';
import SectionHeader from '@moment/components/SectionHeader/SectionHeader.tsx';
import { HoroscopesLayout } from '@moment/layouts/Horoscopes/HoroscopesLayout.tsx';
import { HoroscopesV2Wrapper } from '@moment/components/HoroscopesV2/HoroscopesV2Wrapper/HoroscopesV2Wrapper.tsx';
import Outbrain from '@moment/components/Outbrain/Outbrain.tsx';
import Footer from '@moment/components/Footer/Footer.tsx';
import { AdhesionAdWidget, AdPlacementWidget } from './ad-widgets.tsx';
import WeatherWrapper from '@moment/components/Weather/WeatherWrapper.tsx';
import IFrame from '@moment/components/IFrame/IFrame.tsx';
import ThirdPartyScript from '@moment/components/ThirdPartyScript/ThirdPartyScript.tsx';
import ThirdPartyEmbed from '@moment/components/ThirdPartyEmbed/ThirdPartyEmbed.tsx';

// Define the types for our widgets
export type WidgetType =
  | 'Header'
  | 'Text'
  | 'Image'
  | 'Jokes'
  | 'WordOfDay'
  | 'Quotes'
  | 'FortuneCookie'
  | 'Carousel'
  | 'SectionHeader'
  | 'Horoscopes'
  | 'Outbrain'
  | 'Footer'
  | 'AdPlacement'
  | 'AdhesionAd'
  | 'Weather'
  | 'IFrame'
  | 'HoroscopesV2Wrapper'
  | 'ThirdPartyScript'
  | 'ThirdPartyEmbed';

// Define the structure for a property definition
export interface PropDefinition {
  name: string;
  label: string;
  type: 'string' | 'boolean' | 'number' | 'dropdown' | 'hidden'; // Can be expanded for more types
  defaultValue: string | boolean | number;
  category?: string;
  options?: string[] | Array<{ key: string; value: string }>;
}

// --- Discriminated Union for Layout Items ---

export interface WidgetInstance {
  itemType: 'widget';
  instanceId: string; // A unique ID for this specific instance
  type: WidgetType;
  props: Record<string, any>;
}

export interface FragmentInstance {
  itemType: 'fragment';
  instanceId: string; // A unique ID for this specific instance
  fragmentId: string; // The ID of the FeedFragment
  props: {
    trending?: boolean; // Trending flag for this instance
    schedule: string; // Overridden schedule for this instance
    publisher?: string; // Publisher ID for this instance
    source: 'schedule' | 'publisher' | 'personalized-feed'; // Source of the fragment
  };
}

export type LayoutItem = WidgetInstance | FragmentInstance;

// --- Widget Components (Updated to accept props) ---
const TextWidget = ({ content }: { content: string }) => (
  <div
    style={{
      padding: '1rem',
      backgroundColor: '#e8f5e9',
      border: '1px solid #a5d6a7',
      borderRadius: '4px',
    }}
  >
    <p style={{ margin: 0 }}>{content || 'Text block content...'}</p>
  </div>
);

const ImageWidget = ({ altText }: { altText: string }) => (
  <div
    style={{
      padding: '1rem',
      backgroundColor: '#f3e5f5',
      border: '1px solid #ce93d8',
      borderRadius: '4px',
      textAlign: 'center',
    }}
  >
    <svg
      width="100%"
      height="80"
      style={{ backgroundColor: '#e1bee7', borderRadius: '4px' }}
    >
      <text
        x="50%"
        y="50%"
        dominantBaseline="middle"
        textAnchor="middle"
        fill="#6a1b9a"
        fontSize="16"
      >
        Image Placeholder
      </text>
    </svg>
    <p style={{ margin: '8px 0 0 0' }}>{altText || 'Image Widget'}</p>
  </div>
);

// --- REFACTORED: Single Source of Truth for Widgets ---

export interface WidgetConfig {
  id: WidgetType;
  name: string;
  component: React.FC<any>; // Using `any` to accommodate different prop sets
  propsDefinition?: PropDefinition[]; // NEW: Add prop definitions
}

export const WIDGETS_CONFIG: WidgetConfig[] = [
  {
    id: 'Header',
    name: 'Header',
    component: SectionHeader,
    propsDefinition: [
      {
        name: 'title',
        label: 'Title',
        type: 'string',
        defaultValue: 'Default Header Title',
      },
    ],
  },
  {
    id: 'Text',
    name: 'Text Block',
    component: TextWidget,
    propsDefinition: [
      {
        name: 'content',
        label: 'Content',
        type: 'string',
        defaultValue:
          'This is a text block widget. Lorem ipsum dolor sit amet...',
      },
    ],
  },
  {
    id: 'Image',
    name: 'Image',
    component: ImageWidget,
    propsDefinition: [
      {
        name: 'altText',
        label: 'Alt Text',
        type: 'string',
        defaultValue: 'Descriptive alt text',
      },
    ],
  },
  {
    id: 'Horoscopes',
    name: 'Horoscopes',
    component: HoroscopesLayout,
    propsDefinition: [
      {
        name: 'showEdge',
        label: 'Show Top Banner',
        type: 'boolean',
        defaultValue: true,
      },
      {
        name: 'showCards',
        label: 'Show Category Cards',
        type: 'boolean',
        defaultValue: true,
      },
      {
        name: 'showGrid',
        label: 'Show Zodiac Grid',
        type: 'boolean',
        defaultValue: true,
      },
    ],
  },
  {
    id: 'HoroscopesV2Wrapper',
    name: 'HoroscopesV2Wrapper',
    component: HoroscopesV2Wrapper,
    propsDefinition: [],
  },
  {
    id: 'Jokes',
    name: 'JokesSwipeWrapperContainer',
    component: JokesSwipeWrapperContainer,
    propsDefinition: [
      {
        name: 'feedType',
        category: 'reveal_widget_schedules',
        label: 'Softbox Schedule',
        type: 'dropdown',
        defaultValue: 'jokes',
        options: ['jokes', 'quotes', 'word-of-day', 'fortune-cookie'],
      },
    ],
  },
  {
    id: 'WordOfDay',
    name: 'WordOfTheDayWrapper',
    component: WordOfTheDayWrapper,
    propsDefinition: [
      {
        name: 'feedType',
        category: 'reveal_widget_schedules',
        label: 'Softbox Schedule',
        type: 'dropdown',
        defaultValue: 'jokes',
        options: ['jokes', 'quotes', 'word-of-day', 'fortune-cookie'],
      },
      {
        name: 'showHeader',
        label: 'toggle header',
        type: 'boolean',
        defaultValue: true,
      },
    ],
  },
  {
    id: 'Quotes',
    name: 'QuotesWrapper',
    component: QuotesWrapper,
    propsDefinition: [
      {
        name: 'feedType',
        category: 'reveal_widget_schedules',
        label: 'Softbox Schedule',
        type: 'dropdown',
        defaultValue: 'jokes',
        options: ['jokes', 'quotes', 'word-of-day', 'fortune-cookie'],
      },
    ],
  },
  {
    id: 'FortuneCookie',
    name: 'FortuneCookieWrapper',
    component: FortuneCookieWrapper,
    propsDefinition: [
      {
        name: 'feedType',
        category: 'reveal_widget_schedules',
        label: 'Softbox Schedule',
        type: 'dropdown',
        defaultValue: 'jokes',
        options: ['jokes', 'quotes', 'word-of-day', 'fortune-cookie'],
      },
    ],
  },
  {
    id: 'Carousel',
    name: 'CarouselWrapper',
    component: CarouselWrapper,
    propsDefinition: [
      {
        name: 'feedType',
        category: 'carousel_schedules',
        label: 'Softbox Schedule',
        type: 'dropdown',
        defaultValue: 'jokes',
        options: ['jokes', 'quotes', 'word-of-day', 'fortune-cookie'],
      },
      {
        name: 'trending',
        label: 'Trending',
        type: 'boolean',
        defaultValue: false,
      },
    ],
  },
  {
    id: 'Outbrain',
    name: 'Outbrain',
    component: Outbrain,
    propsDefinition: [
      {
        name: 'widgetId',
        label: 'Outbrain widget',
        type: 'dropdown',
        defaultValue: 'JS_9',
        options: ['JS_9', 'JS_13'],
      },
      {
        name: 'hideHeader',
        label: 'Hide header',
        type: 'boolean',
        defaultValue: false,
      },
    ],
  },
  {
    id: 'SectionHeader',
    name: 'Section Header',
    component: SectionHeader,
    propsDefinition: [
      {
        name: 'text',
        label: 'Text for header',
        type: 'string',
        defaultValue: 'HEADER',
      },
    ],
  },
  {
    id: 'Footer',
    name: 'Footer',
    component: Footer,
    propsDefinition: [],
  },
  {
    id: 'AdPlacement',
    name: 'Ad Placement',
    component: AdPlacementWidget,
    propsDefinition: [
      {
        name: 'placement',
        category: 'ad_placements',
        label: 'Ad Placement',
        type: 'dropdown',
        defaultValue: 'top',
        options: ['top', 'bot'],
      },
    ],
  },
  {
    id: 'AdhesionAd',
    name: 'Adhesion Ad',
    component: AdhesionAdWidget,
    propsDefinition: [
      {
        name: 'hideInApp',
        label: 'Hide in App',
        type: 'boolean',
        defaultValue: false,
      },
    ],
  },
  {
    id: 'Weather',
    name: 'Weather',
    component: WeatherWrapper,
    propsDefinition: [],
  },
  {
    id: 'IFrame',
    name: 'IFrame',
    component: IFrame,
    propsDefinition: [
      {
        name: 'src',
        type: 'string',
        label: 'Source URL',
        defaultValue: false,
      },
      {
        name: 'height',
        type: 'string',
        label: 'Height of iframe',
        defaultValue: false,
      },
      {
        name: 'width',
        type: 'string',
        label: 'Width of iframe',
        defaultValue: '100%',
      },
      {
        name: 'border',
        type: 'boolean',
        label: 'Display iframe border',
        defaultValue: false,
      },
      {
        name: 'title',
        type: 'string',
        label: 'Title of iframe',
        defaultValue: false,
      },
      {
        name: 'containerClass',
        type: 'string',
        label: 'Class name of iframe',
        defaultValue: false,
      },
      {
        name: 'allowPopups',
        type: 'boolean',
        label: 'Popups permitted in iframe',
        defaultValue: false,
      },
      {
        name: 'allowScripts',
        type: 'boolean',
        label: 'Scripts permitted in iframe',
        defaultValue: false,
      },
      {
        name: 'allowTopNavigationByActivation',
        type: 'boolean',
        label: 'Top navigation permitted by iframe',
        defaultValue: false,
      },
      {
        name: 'notForDnt',
        type: 'boolean',
        label: 'Not for DNT (not an advertising script — renders even when user has Do Not Track enabled)',
        defaultValue: false,
      },
    ],
  },
  {
    id: 'ThirdPartyScript',
    name: 'Third-Party Script',
    component: ThirdPartyScript,
    propsDefinition: [
      {
        name: 'scriptSrc',
        type: 'string',
        label: 'Script URL or tag',
        defaultValue: '',
      },
      {
        name: 'async',
        type: 'boolean',
        label: 'Load async',
        defaultValue: true,
      },
      {
        name: 'notForDnt',
        type: 'boolean',
        label: 'Not for DNT (not an advertising script — runs even when user has Do Not Track enabled)',
        defaultValue: false,
      },
    ],
  },
  {
    id: 'ThirdPartyEmbed',
    name: 'Third-Party Embed',
    component: ThirdPartyEmbed,
    propsDefinition: [
      {
        name: 'snippet',
        type: 'string',
        label: 'HTML embed snippet',
        defaultValue: '',
      },
      {
        name: 'async',
        type: 'boolean',
        label: 'Load async',
        defaultValue: true,
      },
      {
        name: 'notForDnt',
        type: 'boolean',
        label: 'Not for DNT (not an advertising script — runs even when user has Do Not Track enabled)',
        defaultValue: false,
      },
    ],
  },
];

// Create a map for easy and performant component lookup.
export const WIDGET_COMPONENT_MAP = WIDGETS_CONFIG.reduce(
  (acc, widget) => {
    acc[widget.id] = widget.component;
    return acc;
  },
  {} as Record<WidgetType, React.FC<any>>
);
